import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import printPdf from 'utils/printPdf';
import { Helmet } from 'react-helmet';
import qs from 'qs';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import DocumentPreviewFillForm from 'components/common/DocumentPreviewFillForm';

import { createPatientDocument } from 'redux/modules/patientsDocuments';
import { createOrderLineDocument } from 'redux/modules/ordersLinesDocuments';
import { addNotification } from 'redux/modules/notifications';
import { fetchUser } from 'redux/modules/users';

import documentToPDF from 'utils/documentToPDF';
import formatOrderNumber from 'utils/formatOrderNumber';
import splitOrderLines from 'utils/splitOrderLines';
import documents from 'config/documents';

import { appTitle, titleSeparator } from 'config/helmetSettings';

class OrderPrint extends Component {
  constructor(props) {
    super(props);

    this.state = {
      documentImages: null,
    };

    this.importDependencies = this.importDependencies.bind(this);
  }

  componentDidMount() {
    const { location, history, actions, account } = this.props;
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ isMounted: true });
    this.importDependencies();
    actions.fetchUser({
      ID: account.ID,
      triggerLoading: true,
    });
    if (window.historyState && !location.state) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        state: window.historyState,
      });
    }
  }

  componentWillUnmount() {
    this.setState({ isMounted: false });
  }

  async importDependencies() {
    const { document, appInfo } = this.props;
    const documentImages = [];
    const pageImagesPromises = document.Pages.map((page, index) => (
      import(`../../${page.imageUrl}`)
        .then((image) => {
          documentImages[index] = image.default;
        })));
    Promise.all(pageImagesPromises).then(() => {
      if (this.state.isMounted) {
        this.setState({
          documentImages,
        });
      }
    });
  }

  render() {
    const {
      isCMN,
      document,
      actions,
      printData,
      account,
      currentUser,
      appInfo,
      printNavigationFormValues,
      selectedFormValues,
    } = this.props;
    const { documentImages } = this.state;
    const documentWithImages = document && documentImages ? {
      ...document,
      Pages: document.Pages.map((page, index) => ({
        ...page,
        image: documentImages[index],
      })),
    } : null;
    const params = qs.parse(this.props.location.search, { ignoreQueryPrefix: true });
    let navigationSectionTitle;
    const splitedOrderLines = printData && printData.orderLine
    && document.OrderLineMax && printData.orderLine.length > document.OrderLineMax
      ? splitOrderLines(printData.orderLine, document.OrderLineMax)
      : null;
    switch (document.Type) {
      case 'Delivery Ticket':
        navigationSectionTitle = 'Delivery Tickets';
        break;
      case 'Form 1500':
        navigationSectionTitle = '1500 Forms';
        break;
      case 'Prior Authorization Template':
        navigationSectionTitle = 'Prior Authorization Templates';
        break;
      case 'Assessment Form':
        navigationSectionTitle = 'Assessment Forms';
        break;
      default:
        navigationSectionTitle = document.Type;
        break;
    }
    let initialValues = {};
    if (printData) {
      const fields = document.Pages.reduce((accumulator, currentValue) => ([
        ...accumulator,
        ...currentValue.fields ? currentValue.fields : [],
      ]), []);
      initialValues = fields.reduce((accumulator, currentValue) => ({
        ...accumulator,
        ...{ [currentValue.name]:
          currentValue.getValue
            ? currentValue.getValue({
              patient: printData.patient,
              business: printData.business,
              systemSettings: printData.systemSettings,
              insurance: printData.insurance,
              facility: printData.facility,
              diagnosis: printData.diagnosis,
              orderLine: splitedOrderLines
                ? splitedOrderLines[Number(params.orderLines)]
                : printData.orderLine,
              prescriber: printData.prescriber,
              orderNotes: printData.orderNotes,
              orderInsuranceParam: printData.orderInsuranceParam,
              account: {
                ...account,
                ...currentUser ? { ...currentUser } : {},
              },
            }) : null },
      }), {
        Name: document.Name,
        'Show Charge': true,
        'Show Background Template': true,
      });
    }
    return (
      <Layout
        variant="print"
        navigationSections={[{
          title: navigationSectionTitle,
          items: [{
            title: document.Name,
            href: `/document/${document.ID}`,
          }],
        }]}
        {...printData && printData.orderID ? {
          titleText: `Order ${formatOrderNumber(printData.orderID)}`,
          ...splitedOrderLines ? {
            navigationSections: [{
              title: navigationSectionTitle,
              items: splitedOrderLines.map((orderLines, index) => {
                const orderLineHCPCSCodes = orderLines.map(orderLine =>
                  orderLine['HCPC S Code']['HCPC S Code'] || orderLine['HCPC S Code']).join(', ');
                return {
                  title: `${document.Name} (${orderLineHCPCSCodes})`,
                  id: `Document_${document.ID}_${index}`,
                  href: `/document/${document.ID}`,
                  search: `orderLines=${index}`,
                  state: {
                    printData,
                    isCMN,
                  },
                };
              }),
            }],
          } : {},
        } : {}}
      >
        {(document && documentImages) &&
          <Fragment>
            <Helmet>
              <title>
                {appTitle} {titleSeparator} Print {document.Name}
              </title>
            </Helmet>
            <DocumentPreviewFillForm
              key={splitedOrderLines ? `${document.ID}-${params.orderLines}-documentPreviewFill` : `${document.ID}-documentPreviewFill`}
              form={splitedOrderLines ? `${document.ID}-${params.orderLines}-documentPreviewFill` : `${document.ID}-documentPreviewFill`}
              variant="_blank"
              document={documentWithImages}
              initialValues={initialValues}
              business={printData.business}
              appInfo={appInfo}
              onSubmitCustom={printOnSubmitSuccess =>
                (values) => {
                  const handlePDF = (PDF) => {
                    if (printOnSubmitSuccess) {
                      return {
                        blob: PDF.output('blob'),
                        Name: values.Name,
                        printOnSubmitSuccess,
                      };
                    }
                    return new Promise((resolve, reject) => {
                      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        const fileBlob = PDF.output('blob');
                        fileBlob.lastModifiedDate = new Date();
                        if (isCMN) {
                          actions.createOrderLineDocument({
                            values: {
                              ID: printData.orderLine[0].ID,
                              'Record Number': printData.patient['Record Number'],
                              Document: fileBlob,
                              'CMN/AMB': document.Name,
                            },
                            resolve: () => {
                              actions.addNotification({
                                message: `"${values.Name}.pdf" was saved to Order Line CMN and Documents in ${printData.patient['First Name']}, ${printData.patient['Last Name']} chart`,
                                variant: 'success',
                              });
                              resolve();
                            },
                            reject: () => {
                              actions.addNotification({
                                message: `Failed to save "${values.Name}.pdf"`,
                                variant: 'warning',
                              });
                              reject();
                            },
                          });
                        } else {
                          actions.createPatientDocument({
                            values: {
                              'Record Number': printData.Patient['Record Number'],
                              Document: fileBlob,
                              Name: `${values.Name}.pdf`,
                            },
                            resolve: () => {
                              actions.addNotification({
                                message: `"${values.Name}.pdf" was saved to Documents in ${printData.patient['First Name']}, ${printData.patient['Last Name']} chart`,
                                variant: 'success',
                              });
                              resolve();
                            },
                            reject: () => {
                              actions.addNotification({
                                message: `Failed to save "${values.Name}.pdf"`,
                                variant: 'warning',
                              });
                              reject();
                            },
                          });
                        }
                      } else {
                        const file = new File(
                          [PDF.output('blob')],
                          `${values.Name}.pdf`,
                          { type: 'application/pdf', lastModified: Date.now() },
                        );
                        if (isCMN) {
                          actions.createOrderLineDocument({
                            values: {
                              ID: printData.orderLine[0].ID,
                              orderID: printData.orderID,
                              'Record Number': printData.patient['Record Number'],
                              Document: file,
                              'CMN/AMB': document.Name,
                            },
                            resolve: () => {
                              actions.addNotification({
                                message: `"${values.Name}.pdf" was saved to Order Line CMN and Documents in ${printData.patient['First Name']}, ${printData.patient['Last Name']} chart`,
                                variant: 'success',
                              });
                              // Create and trigger event of cmn creatin to parent window
                              const cmnAttachEvent = new CustomEvent('cmnattach', { detail: { 'CMN/AMB': document.Name } });
                              window.opener.dispatchEvent(cmnAttachEvent);
                              resolve();
                            },
                            reject: () => {
                              actions.addNotification({
                                message: `Failed to save "${values.Name}.pdf"`,
                                variant: 'warning',
                              });
                              reject();
                            },
                          });
                        } else {
                          actions.createPatientDocument({
                            values: {
                              'Record Number': printData.patient['Record Number'],
                              Document: file,
                            },
                            resolve: () => {
                              actions.addNotification({
                                message: `"${values.Name}.pdf" was saved to Documents in ${printData.patient['First Name']}, ${printData.patient['Last Name']} chart`,
                                variant: 'success',
                              });
                              resolve();
                            },
                            reject: () => {
                              actions.addNotification({
                                message: `Failed to save "${values.Name}.pdf"`,
                                variant: 'warning',
                              });
                              reject();
                            },
                          });
                        }
                      }
                    });
                  };
                  const printNavigationKeys = printNavigationFormValues
                    ? Object.keys(printNavigationFormValues).filter(item => printNavigationFormValues[item] === true)
                    : [];
                  if (splitedOrderLines && printNavigationKeys.length > 0) {
                    return printNavigationKeys
                      .sort((a, b) => Number(a.split('_')[2]) - Number(b.split('_')[2]))
                      .reduce((accumulator, currentValue) => {
                        const chunkIndex = Number(currentValue.split('_')[2]);
                        const currentFormName = `${document.ID}-${chunkIndex}-documentPreviewFill`;
                        const currentFormValues = selectedFormValues[currentFormName];
                        return accumulator.then(prevPDF => documentToPDF({
                          inputPDF: prevPDF,
                          document: documentWithImages,
                          patient: printData.patient,
                          business: printData.business,
                          systemSettings: printData.systemSettings,
                          insurance: printData.insurance,
                          facility: printData.facility,
                          diagnosis: printData.diagnosis,
                          orderLine: splitedOrderLines[chunkIndex],
                          prescriber: printData.prescriber,
                          referralSource: printData.referralSource,
                          appInfo,
                          account: {
                            ...account,
                            ...currentUser ? { ...currentUser } : {},
                          },
                          ...currentFormValues ? { values: currentFormValues } : {},
                        }));
                      }, Promise.resolve()).then(handlePDF);
                  }
                  return documentToPDF({
                    document: documentWithImages,
                    patient: printData.patient,
                    business: printData.business,
                    systemSettings: printData.systemSettings,
                    insurance: printData.insurance,
                    facility: printData.facility,
                    diagnosis: printData.diagnosis,
                    orderLine: splitedOrderLines
                      ? splitedOrderLines[Number(params.orderLines)]
                      : printData.orderLine,
                    prescriber: printData.prescriber,
                    referralSource: printData.referralSource,
                    appInfo,
                    account: {
                      ...account,
                      ...currentUser ? { ...currentUser } : {},
                    },
                    values,
                  }).then(handlePDF);
                }
              }
              onSubmitSuccess={(values) => {
                if (values.printOnSubmitSuccess) {
                  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(values.blob, `${values.Name}.pdf`);
                  } else {
                    printPdf(values.blob);
                  }
                }
              }}
            />
          </Fragment>
        }
      </Layout>
    );
  }
}

OrderPrint.propTypes = {
  document: PropTypes.shape({
    ID: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
  }),
  account: PropTypes.shape({
    ID: PropTypes.number.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    createPatientDocument: PropTypes.func.isRequired,
    createOrderLineDocument: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    fetchUser: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  printData: PropTypes.shape({
    id: PropTypes.number,
  }),
  isCMN: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  appInfo: PropTypes.shape({
    'APP NAME': PropTypes.string,
  }).isRequired,
  printNavigationFormValues: PropTypes.shape({}),
  selectedFormValues: PropTypes.shape({}),
};

OrderPrint.defaultProps = {
  document: null,
  printData: null,
  isCMN: false,
  printNavigationFormValues: null,
  selectedFormValues: {},
};

const mapStateToProps = (store, ownProps) => {
  const document = documents.find(item => item.ID === Number(ownProps.match.params.id));
  const printNavigationFormValues = getFormValues('printNavigationForm')(store);
  const printNavigationKeys = printNavigationFormValues
    ? Object.keys(printNavigationFormValues).filter(item => printNavigationFormValues[item] === true)
    : [];
  const selectedFormValues = (document && printNavigationKeys.length > 0)
    ? printNavigationKeys
      .filter(item => item.indexOf('Document') !== -1)
      .reduce((accumulator, currentValue) => {
        const chunkIndex = currentValue.split('_')[2];
        const currentFormName = `${document.ID}-${chunkIndex}-documentPreviewFill`;
        const currentFormValues = getFormValues(currentFormName)(store);
        return {
          ...accumulator,
          ...currentFormValues ? { [currentFormName]: currentFormValues } : {},
        };
      }, {})
    : {};
  return {
    document,
    printData: store.router.location.state ? store.router.location.state.printData : null,
    isCMN: store.router.location.state && store.router.location.state.isCMN,
    appInfo: store.appInfo,
    account: store.account,
    currentUser: store.users.data.find(item => item.ID === store.account.ID),
    printNavigationFormValues,
    selectedFormValues,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    createPatientDocument,
    createOrderLineDocument,
    addNotification,
    fetchUser,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderPrint);



// WEBPACK FOOTER //
// ./src/pages/common/DocumentPrint.js